import { useState, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import { motion } from 'framer-motion';
import { PreTestForm } from '../components/PreTestForm';
import { useUser } from '../hooks/useUser';
import HeroSection from '../components/landing/HeroSection';
import HowItWorks from '../components/landing/HowItWorks';
import ManyRooms from '../components/landing/ManyRooms';
import FeaturesGrid from '../components/landing/FeaturesGrid';
import TrustSection from '../components/landing/TrustSection';
import Footer from '../components/landing/Footer';
import ScrollToTop from '../components/ScrollToTop';
import { LandingHeader } from '../components/landing/LandingHeader';

export default function LandingPage() {
  const router = useRouter();
  const { setUserId, setFirstName, isAuthenticated, authLoading, userProfile } = useUser();
  const [showForm, setShowForm] = useState(false);
  const navigatingRef = useRef(false);
  const primaryCtaLabel = isAuthenticated ? 'Take Another Test' : 'Take the Test';

  const handleStartTest = async () => {
    setShowForm(true);
  };

  const handleFormComplete = useCallback(async (userId: string, firstName: string) => {
    if (navigatingRef.current) return;
    navigatingRef.current = true;

    try {
      console.log('🔄 LandingPage: Setting userId and firstName:', { userId, firstName });
      setUserId(userId);
      setFirstName(firstName);

      await new Promise(resolve => setTimeout(resolve, 100));

      const storedUserId = localStorage.getItem('cubetest-userId');
      const storedToken = localStorage.getItem('user_access_token');

      console.log('✅ LandingPage: Verification before navigation:', {
        expectedUserId: userId,
        storedUserId,
        match: storedUserId === userId,
        hasToken: !!storedToken
      });

      if (storedUserId !== userId) {
        console.error('❌ LandingPage: userId mismatch detected! Retrying...');
        await new Promise(resolve => setTimeout(resolve, 50));
        const retryUserId = localStorage.getItem('cubetest-userId');
        if (retryUserId !== userId) {
          throw new Error('Failed to persist userId to localStorage');
        }
        console.log('✅ LandingPage: Retry successful, userId matched');
      }

      if (router.asPath !== '/test') {
        console.log('🚀 LandingPage: Navigating to /test with verified userId:', userId);
        router.push('/test');
      }
    } catch (error) {
      console.error('❌ Error in handleFormComplete:', error);
      navigatingRef.current = false;
    }
  }, [router, setFirstName, setUserId]);

  const isAuthLoadingState = authLoading && !isAuthenticated && !userProfile;

  return (
    <>
      {showForm ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center justify-center p-4"
        >
          <PreTestForm onComplete={handleFormComplete} />
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="bg-[#f7f6f2] min-h-screen"
        >
          <LandingHeader
            isAuthenticated={isAuthenticated}
            isAuthLoadingState={isAuthLoadingState}
            primaryCtaLabel={primaryCtaLabel}
            userProfile={userProfile}
            onStartTest={handleStartTest}
          />
          <HeroSection onStartTest={handleStartTest} />
          <HowItWorks />
          <ManyRooms />
          <FeaturesGrid />
          <TrustSection onStartTest={handleStartTest} />
          <Footer />
          <ScrollToTop />

          {/* Sticky mobile CTA: the thumb lives at the bottom of the screen */}
          <div className="fixed bottom-0 inset-x-0 z-40 p-3 md:hidden bg-gradient-to-t from-[#f7f6f2] via-[#f7f6f2]/95 to-transparent pointer-events-none">
            <button
              onClick={handleStartTest}
              className="pointer-events-auto w-full bg-[#c43b3b] text-white font-bold py-3.5 rounded-full text-lg shadow-[0_6px_18px_rgba(196,59,59,0.35)] active:bg-[#b03434]"
            >
              Take the Test, Free
            </button>
          </div>
        </motion.div>
      )}
    </>
  );
}
